import React from 'react';
import { Product } from '../types';

// Helper function to ensure URLs are absolute
const ensureAbsoluteUrl = (url: string, baseUrl: string = 'https://goodcart.org'): string => {
  // If the URL already starts with http:// or https://, it's already absolute
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If the URL starts with a slash, append it to the base URL
  if (url.startsWith('/')) {
    return `${baseUrl}${url}`;
  }
  
  // Otherwise, assume it's a relative path and add a slash
  return `${baseUrl}/${url}`;
};

interface ProductStructuredDataProps {
  product: Product;
  url: string;
}

export const ProductStructuredData: React.FC<ProductStructuredDataProps> = ({ product, url }) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: ensureAbsoluteUrl(product.image),
    sku: product.id,
    mpn: product.id,
    brand: {
      '@type': 'Brand',
      name: 'GoodCart'
    },
    offers: {
      '@type': 'Offer',
      url: url,
      priceCurrency: 'USD',
      price: product.price,
      priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'GoodCart'
      }
    }
    // Note: aggregateRating and review removed to comply with Google's structured data guidelines.
    // Only add these properties when real, verified review data is available.
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

interface OrganizationStructuredDataProps {
  url?: string;
}

export const OrganizationStructuredData: React.FC<OrganizationStructuredDataProps> = ({ 
  url = 'https://goodcart.org' 
}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GoodCart',
    url: url,
    logo: `${url}/logo192.png`,
    sameAs: [
      'https://x.com/goodcartorg'
    ],
    // TODO: Replace with real contact information or remove
    // contactPoint: {
    //   '@type': 'ContactPoint',
    //   telephone: '+1-800-123-4567',
    //   contactType: 'customer service',
    //   availableLanguage: 'English'
    // },
    description: 'GoodCart helps you shop smarter by connecting you with vetted, health-conscious products with clean ingredients.'
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

interface WebsiteStructuredDataProps {
  url?: string;
}

export const WebsiteStructuredData: React.FC<WebsiteStructuredDataProps> = ({ 
  url = 'https://goodcart.org' 
}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'GoodCart',
    url: url,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};

const StructuredDataComponents = {
  ProductStructuredData,
  OrganizationStructuredData,
  WebsiteStructuredData
};

export default StructuredDataComponents;
